import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from '@themesberg/react-bootstrap';
import { Link } from 'react-router-dom';
import PageHeader, { AlertMessage, StatusBadge } from '../../components/PageHeader';
import { decideApi, rulesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Routes } from '../../routes';

const presets = {
  login: {
    label: 'Login attempt',
    payload: {
      eventType: 'auth.login_attempt',
      entity: { type: 'user', id: 'user_789' },
      subjects: {
        userId: 'user_789',
        email: 'user@example.com',
        phone: '+15555550100',
        deviceId: 'dev_abc',
        ip: '1.2.3.4',
        sessionId: 'sess_001',
      },
      attributes: { country: 'US', channel: 'web', result: 'failed' },
      value: { amount: null, currency: null },
    },
  },
  withdrawal: {
    label: 'Withdrawal (15,000 USD)',
    payload: {
      eventType: 'payout.request',
      entity: { type: 'account', id: 'acct_1001' },
      subjects: {
        customerId: 'cust_42',
        userId: 'user_789',
        deviceId: 'dev_abc',
        ip: '1.2.3.4',
      },
      attributes: { country: 'US', channel: 'mobile', result: 'pending' },
      value: { amount: 15000, currency: 'USD' },
    },
  },
  withdrawalBlock: {
    label: 'Withdrawal (40,000 USD — may block)',
    payload: {
      eventType: 'payout.request',
      entity: { type: 'account', id: 'acct_1001' },
      subjects: {
        customerId: 'cust_42',
        userId: 'user_789',
        deviceId: 'dev_abc',
        ip: '1.2.3.4',
      },
      attributes: { country: 'US', channel: 'mobile', result: 'pending' },
      value: { amount: 40000, currency: 'USD' },
    },
  },
  blocklistHit: {
    label: 'Blocklist hit (email)',
    payload: {
      eventType: 'auth.login_attempt',
      entity: { type: 'user', id: 'user_blocked' },
      subjects: {
        userId: 'user_blocked',
        email: 'fraud@example.com',
        ip: '1.2.3.4',
      },
      attributes: { country: 'US', channel: 'web', result: 'failed' },
      value: { amount: null, currency: null },
    },
  },
  memberRegistration: {
    label: 'Member registration (ghost-user check)',
    payload: {
      eventType: 'member.registration',
      entity: { type: 'member', id: 'mem_1001' },
      subjects: {
        memberId: 'mem_1001',
        nationalId: 'ID-998877',
        phone: '+15555550100',
        email: 'user@example.com',
        bankAccount: 'US00BANK123456',
        deviceId: 'dev_abc',
      },
      attributes: {
        createdBy: 'EMP042',
        branch: 'Nairobi',
        country: 'US',
        channel: 'branch',
      },
      value: { amount: null, currency: null },
    },
  },
  transactionBackdate: {
    label: 'Backdated transaction (small)',
    payload: {
      eventType: 'transaction.backdate',
      entity: { type: 'transaction', id: 'txn_5001' },
      subjects: {
        transactionId: 'txn_5001',
        createdBy: 'EMP82',
        accountId: 'acct_1001',
      },
      attributes: {
        transactionDate: '2026-07-31',
        systemDate: '2026-08-15',
        daysBackdated: '15',
        branch: 'Nairobi',
      },
      value: { amount: 25000, currency: 'KES' },
    },
  },
  transactionBackdateLarge: {
    label: 'Backdated transaction (>100,000)',
    payload: {
      eventType: 'transaction.backdate',
      entity: { type: 'transaction', id: 'txn_5002' },
      subjects: {
        transactionId: 'txn_5002',
        createdBy: 'EMP82',
        accountId: 'acct_1001',
      },
      attributes: {
        transactionDate: '2026-07-31',
        systemDate: '2026-08-15',
        daysBackdated: '15',
        branch: 'Nairobi',
      },
      value: { amount: 150000, currency: 'KES' },
    },
  },
  paymentAuthorize: {
    label: 'Card authorize (gateway)',
    payload: {
      eventType: 'payment.authorize',
      entity: { type: 'payment', id: 'pay_9001' },
      subjects: {
        panHash: 'pan_abc123',
        merchantId: 'mid_100',
        ip: '203.0.113.10',
        deviceId: 'dev_fp_778',
        email: 'user@example.com',
        bin: '411111',
      },
      attributes: {
        channel: 'web',
        country: 'US',
        currency: 'USD',
      },
      value: { amount: 49.99, currency: 'USD' },
    },
  },
  paymentRefund: {
    label: 'Card refund (gateway)',
    payload: {
      eventType: 'payment.refund',
      entity: { type: 'payment', id: 'pay_9001' },
      subjects: {
        panHash: 'pan_abc123',
        merchantId: 'mid_100',
        ip: '203.0.113.10',
      },
      attributes: {
        channel: 'web',
        country: 'US',
      },
      value: { amount: 49.99, currency: 'USD' },
    },
  },
};

function buildPayload(form, tenantId) {
  const subjects = {};
  form.subjects.split('\n').forEach((line) => {
    const idx = line.indexOf('=');
    if (idx > 0) {
      const key = line.slice(0, idx).trim();
      const val = line.slice(idx + 1).trim();
      if (key && val) subjects[key] = val;
    }
  });

  const attributes = {};
  form.attributes.split('\n').forEach((line) => {
    const idx = line.indexOf('=');
    if (idx > 0) {
      const key = line.slice(0, idx).trim();
      const val = line.slice(idx + 1).trim();
      if (key && val) attributes[key] = val;
    }
  });

  return {
    tenantId: tenantId || null,
    eventType: form.eventType,
    entity: { type: form.entityType, id: form.entityId },
    subjects,
    attributes,
    value: {
      amount: form.amount === '' ? null : Number(form.amount),
      currency: form.currency || null,
    },
    timestamp: new Date(form.timestamp).toISOString(),
  };
}

function presetToForm(preset, tenantId) {
  const p = preset.payload;
  const subjects = Object.entries(p.subjects || {}).map(([k, v]) => `${k}=${v}`).join('\n');
  const attributes = Object.entries(p.attributes || {}).map(([k, v]) => `${k}=${v}`).join('\n');
  return {
    eventType: p.eventType,
    entityType: p.entity.type,
    entityId: p.entity.id,
    subjects,
    attributes,
    amount: p.value && p.value.amount != null ? String(p.value.amount) : '',
    currency: (p.value && p.value.currency) || '',
    timestamp: new Date().toISOString().slice(0, 16),
    tenantId,
  };
}

export default function Decide() {
  const { tenantId } = useAuth();
  const [form, setForm] = useState(presetToForm(presets.login, tenantId));
  const [result, setResult] = useState(null);
  const [httpStatus, setHttpStatus] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ruleCount, setRuleCount] = useState(null);
  const [dismissRulesHint, setDismissRulesHint] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rulesApi
      .list()
      .then(({ data }) => {
        if (!cancelled) setRuleCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {
        if (!cancelled) setRuleCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const applyPreset = (key) => {
    setForm(presetToForm(presets[key], tenantId));
    setResult(null);
    setHttpStatus(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const payload = buildPayload(form, tenantId);
      const { data, status } = await decideApi.evaluate(payload);
      setResult(data);
      setHttpStatus(status);
    } catch (err) {
      if (err.status === 429 && err.body) {
        setResult(err.body);
        setHttpStatus(429);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const noRulesYet = ruleCount === 0;
  const emptyRuleResults =
    result &&
    !result.blocklistHit &&
    Array.isArray(result.results) &&
    result.results.length === 0;

  return (
    <>
      <PageHeader title="Decision" subtitle="POST /v1/decide — blocklist first, then velocity rules." />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />

      {noRulesYet && !dismissRulesHint ? (
        <AlertMessage
          variant="warning"
          onClose={() => setDismissRulesHint(true)}
          message={
            <>
              You have not created any velocity rules yet. Decisions will return{' '}
              <strong>ALLOW</strong> with an empty <code>results</code> array unless a
              blocklist entry matches — useful for wiring checks, but not a real rule test.{' '}
              <Link to={Routes.Rules.path} className="alert-link">
                Create a rule first
              </Link>
              .
            </>
          }
        />
      ) : null}

      <Row className="mb-3">
        <Col>
          {Object.entries(presets).map(([key, preset]) => (
            <Button key={key} size="sm" variant="outline-primary" className="me-2 mb-2" onClick={() => applyPreset(key)}>
              {preset.label}
            </Button>
          ))}
        </Col>
      </Row>

      <Row>
        <Col lg={6} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Event type</Form.Label>
                  <Form.Control required value={form.eventType} onChange={(e) => update('eventType', e.target.value)} />
                </Form.Group>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Entity type</Form.Label>
                    <Form.Control required value={form.entityType} onChange={(e) => update('entityType', e.target.value)} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Entity id</Form.Label>
                    <Form.Control required value={form.entityId} onChange={(e) => update('entityId', e.target.value)} />
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Subjects (key=value, one per line)</Form.Label>
                  <Form.Control as="textarea" rows={5} value={form.subjects} onChange={(e) => update('subjects', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Attributes (key=value, one per line)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={form.attributes} onChange={(e) => update('attributes', e.target.value)} />
                </Form.Group>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control type="number" value={form.amount} onChange={(e) => update('amount', e.target.value)} placeholder="optional" />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Form.Control value={form.currency} onChange={(e) => update('currency', e.target.value)} placeholder="USD" />
                  </Col>
                </Row>
                <Form.Group className="mb-4">
                  <Form.Label>Timestamp</Form.Label>
                  <Form.Control type="datetime-local" value={form.timestamp} onChange={(e) => update('timestamp', e.target.value)} />
                </Form.Group>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Evaluating…' : 'Evaluate decision'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h5 className="mb-3">Response</h5>
              {!result ? (
                <p className="text-gray">Submit an event to see the decision outcome.</p>
              ) : (
                <>
                  <p>
                    HTTP <strong>{httpStatus}</strong>{' '}
                    <StatusBadge value={result.decision} />
                  </p>
                  {emptyRuleResults ? (
                    <div className="alert alert-info small">
                      No velocity rules qualified for this event
                      {noRulesYet
                        ? ' (you have no rules yet)'
                        : ' (none matched this eventType / groupBy)'}
                      .{' '}
                      {noRulesYet ? (
                        <Link to={Routes.Rules.path}>Add a rule</Link>
                      ) : (
                        <>Check the rule&apos;s event type and group-by field, or{' '}
                          <Link to={Routes.Rules.path}>manage rules</Link>.</>
                      )}
                    </div>
                  ) : null}
                  {result.blocklistHit ? (
                    <div className="alert alert-danger">
                      Blocklist hit on <code>{result.blocklistHit.subjectType}</code>
                      {result.blocklistHit.reason ? `: ${result.blocklistHit.reason}` : ''}
                    </div>
                  ) : null}
                  {result.results && result.results.length > 0 ? (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Rule</th>
                            <th>Triggered</th>
                            <th>Compared</th>
                            <th>Decision</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.results.map((r) => (
                            <tr key={r.ruleId}>
                              <td>{r.ruleName}</td>
                              <td>{r.triggered ? 'yes' : 'no'}</td>
                              <td>
                                {r.proposedValue != null ? (
                                  <>
                                    <strong>{r.proposedValue}</strong>
                                    {' '}{r.operator}{' '}{r.threshold}
                                    {r.eventValue != null && r.eventValue !== r.proposedValue ? (
                                      <span className="text-muted small d-block">
                                        window {r.currentValue} + event {r.eventValue}
                                      </span>
                                    ) : null}
                                  </>
                                ) : (
                                  r.currentValue
                                )}
                              </td>
                              <td>{r.ruleDecision}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  <pre className="bg-light p-3 rounded small mb-0">{JSON.stringify(result, null, 2)}</pre>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
